import React, { useState } from 'react';
import { Dialog } from '@mui/material';
import { DateField, SmartSelect } from '../dropdown';
import { SidePanel } from '../panel/SidePanel';
import { FormField } from '../panel/FormField';
import type { ResolutionType } from '@techgitter/contracts';
import './reconciliationPanel.css';

/** Resolution-type choices, matching the cr001_resolution_type optionset. */
const RESOLUTION_OPTIONS: { value: ResolutionType; label: string }[] = [
  { value: 'adjustVendorSubscription', label: 'Adjust Vendor Subscription' },
  { value: 'adjustCommercialSubscription', label: 'Adjust Commercial Subscription' },
  { value: 'externalVendorAction', label: 'External Vendor Action' },
  { value: 'acceptVendorAsCorrect', label: 'Accept Vendor As Correct' },
  { value: 'ignore', label: 'Ignore' },
];

export interface ResolveRequest {
  resolutionType: ResolutionType;
  resolutionNotes: string;
  /** Optional future date the resolution can be actioned on (variances only). */
  actionDate?: string | null;
}

interface Props {
  open: boolean;
  title: string;
  subtitle?: string;
  /**
   * Show the optional Action Date field. Variances only — a future date defers
   * the break (keeps it suppressed with this explanation until then, after which
   * it reopens if still unresolved). Exceptions don't support deferral.
   */
  showActionDate?: boolean;
  /**
   * Optional "fix the commitment as part of resolving" action — e.g. a seat
   * break where we can set the subscription seats to the vendor-reported value.
   * When present, a checkbox (default on) runs `run()` before the resolve, and
   * the resolution type defaults to Adjust Vendor Subscription.
   */
  applyAction?: { label: string; run: () => Promise<void> };
  /** Prefill the resolution type (e.g. a guided term-mismatch fix). */
  initialResolutionType?: ResolutionType;
  /** Prefill the resolution notes. */
  initialNotes?: string;
  /** Prefill the Action Date (YYYY-MM-DD); only used when `showActionDate`. */
  initialActionDate?: string;
  /** Performs the resolve API call. Throws on failure. */
  onResolve: (request: ResolveRequest) => Promise<void>;
  onClose: () => void;
  /** Called after a successful resolve so the parent can refresh. */
  onResolved: () => void;
}

/**
 * Shared resolve dialog for variances and exceptions. Captures the structured
 * outcome (resolution type + required notes) the backend records along with the
 * resolving user and timestamp. No auto-resolution — this is the only path that
 * closes a variance/exception.
 */
export const ResolveDialog: React.FC<Props> = ({
  open,
  title,
  subtitle,
  showActionDate = false,
  applyAction,
  initialResolutionType,
  initialNotes,
  initialActionDate,
  onResolve,
  onClose,
  onResolved,
}) => {
  const defaultType: ResolutionType | '' =
    initialResolutionType ?? (applyAction ? 'adjustVendorSubscription' : '');
  const [resolutionType, setResolutionType] = useState<ResolutionType | ''>(defaultType);
  const [notes, setNotes] = useState(initialNotes ?? '');
  const [actionDate, setActionDate] = useState(initialActionDate ?? '');
  const [applyCommitment, setApplyCommitment] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setResolutionType(defaultType);
    setNotes(initialNotes ?? '');
    setActionDate(initialActionDate ?? '');
    setApplyCommitment(true);
    setError(null);
    setSaving(false);
  };

  const handleClose = () => {
    if (saving) return;
    reset();
    onClose();
  };

  const canSubmit = resolutionType !== '' && notes.trim().length > 0 && !saving;

  const handleSubmit = async () => {
    if (resolutionType === '' || !notes.trim()) return;
    setSaving(true);
    setError(null);
    try {
      // Optionally fix the commitment first (e.g. bump subscription seats to the
      // vendor-reported value), then record the resolution.
      if (applyAction && applyCommitment) {
        await applyAction.run();
      }
      await onResolve({
        resolutionType,
        resolutionNotes: notes.trim(),
        actionDate: showActionDate && actionDate ? actionDate : null,
      });
      reset();
      onResolved();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to resolve');
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={saving ? undefined : handleClose}
      maxWidth="sm"
      fullWidth
      // No DialogTitle (the SidePanel header carries the title), so name the
      // dialog explicitly for assistive tech.
      aria-label={title}
      // Strip the Dialog's own paper chrome — the SidePanel card provides the
      // visible surface (radius, shadow); the Dialog only supplies the centred
      // overlay, backdrop, focus trap, and Escape handling.
      PaperProps={{
        sx: {
          background: 'transparent',
          boxShadow: 'none',
          borderRadius: 0,
          overflow: 'visible',
        },
      }}
    >
      <SidePanel
        title={title}
        submitLabel={saving ? 'Resolving…' : 'Resolve'}
        isValid={canSubmit}
        onClose={handleClose}
        onSubmit={handleSubmit}
      >
        {error && <div className="side-panel__alert side-panel__alert--error">{error}</div>}

        {subtitle && <p className="recon-dialog__hint">{subtitle}</p>}

        {applyAction && (
          <label className="side-panel__checkbox">
            <input
              type="checkbox"
              checked={applyCommitment}
              onChange={(e) => setApplyCommitment(e.target.checked)}
            />
            Update the commitment: {applyAction.label}
          </label>
        )}

        <SmartSelect
          label="Resolution type *"
          value={resolutionType || null}
          options={RESOLUTION_OPTIONS}
          onChange={(v) => setResolutionType((v ?? '') as ResolutionType | '')}
          placeholder="Select a resolution type…"
        />

        <FormField label="Resolution notes *">
          <textarea
            rows={3}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="What was investigated and the outcome…"
          />
        </FormField>

        {showActionDate && (
          <DateField
            label="Action date (optional)"
            value={actionDate || null}
            onChange={(iso) => setActionDate(iso ?? '')}
            helperText="If the fix can only be actioned later, set the date. The break is deferred (kept with this explanation) until then, then reopens if still unresolved."
          />
        )}
      </SidePanel>
    </Dialog>
  );
};

export default ResolveDialog;
